import React, { useState, useRef } from 'react';
import { API_BASE } from '../config';

function renderMarkdown(text) {
  if (!text) return '';
  return text
    .replace(/^### (.+)$/gm, '<h4>$1</h4>')
    .replace(/^## (.+)$/gm, '<h3>$1</h3>')
    .replace(/^# (.+)$/gm, '<h2>$1</h2>')
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    .replace(/^- (.+)$/gm, '<li>$1</li>')
    .replace(/(<li>.*<\/li>)/gs, '<ul>$1</ul>')
    .replace(/^---$/gm, '<hr/>')
    .replace(/\n\n/g, '<br/><br/>')
    .replace(/\n/g, '<br/>');
}

function parseSections(text) {
  if (!text) return [];
  const matches = text.match(/^#{1,2} .+$/gm);
  return matches ? matches.map(m => m.replace(/^#+\s*/, '')) : [];
}

const OMENS = [
  { name: 'Dread Check', emoji: '👁️', cmd: '!dreadcheck', desc: 'Check a player\'s accumulated Dread level. Higher Dread = unreliable narration, secret rolls, disadvantage.' },
  { name: 'Whisper', emoji: '🕯️', cmd: '!whisper', desc: 'Send a secret message only one player can see. Paranoia fuel.' },
];

const ENEMIES = ['slappy','furniture','rug','shadow','geometry','echo','memory','remnant','angle','mimic','crawling claw','specter','gibbering mouther','poltergeist','will-o-wisp','nothic','intellect devourer','beholder','beholder zombie','wraith','banshee','flameskull','invisible stalker','the lonely','oblex','bodak','star spawn mangler','boneclaw','the grasp','the hallway','reflection','the furniture','threshold guardian','bilge keeper','the cook','drowned captain','depth horror','the measure'];

export default function SidePanel({ campaignNotes, onCommand, isPro, isProPlus, players, isDM, currentUserId }) {
  const [open, setOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('campaign');
  const [aiResult, setAiResult] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [equipPending, setEquipPending] = useState(null); // item name waiting for player selection
  const contentRef = useRef(null);

  const sections = parseSections(campaignNotes);

  const scrollToSection = (section) => {
    if (!contentRef.current) return;
    const headings = contentRef.current.querySelectorAll('h2, h3, h4');
    const match = Array.from(headings).find(h => h.textContent === section);
    if (match) match.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleTabClick = (tab) => {
    if (open && activeTab === tab) {
      setOpen(false);
    } else {
      setActiveTab(tab);
      setOpen(true);
    }
  };

  return (
    <>
      {/* Tab handles on right edge */}
      <div className={`side-tab-strip ${open ? 'open' : ''}`}>
        <a href="/guild/drowned-kings-court" target="_blank" rel="noopener noreferrer" className={`side-tab-handle ${!isProPlus ? 'tab-locked' : ''}`} style={{ textDecoration: 'none' }}>
          <span>🏰</span>{!isProPlus && <span className="tab-lock">🔒</span>}
        </a>
        <div className={`side-tab-handle ${activeTab === 'cartography' && open ? 'active' : ''} ${!isProPlus ? 'tab-locked' : ''}`} onClick={() => isProPlus && handleTabClick('cartography')}>
          <span>🗺️</span>{!isProPlus && <span className="tab-lock">🔒</span>}
        </div>
        <div className={`side-tab-handle ${activeTab === 'campaign' && open ? 'active' : ''} ${!isProPlus ? 'tab-locked' : ''}`} onClick={() => isProPlus && handleTabClick('campaign')}>
          <span>📖</span>{!isProPlus && <span className="tab-lock">🔒</span>}
        </div>
        <div className={`side-tab-handle ${activeTab === 'menagerie' && open ? 'active' : ''} ${!isProPlus ? 'tab-locked' : ''}`} onClick={() => isProPlus && handleTabClick('menagerie')}>
          <span>👹</span>{!isProPlus && <span className="tab-lock">🔒</span>}
        </div>
        <div className={`side-tab-handle ${activeTab === 'omens' && open ? 'active' : ''} ${!isPro ? 'tab-locked' : ''}`} onClick={() => isPro && handleTabClick('omens')}>
          <span>🌑</span>{!isPro && <span className="tab-lock">🔒</span>}
        </div>
        <div className={`side-tab-handle ${activeTab === 'backpack' && open ? 'active' : ''}`} onClick={() => handleTabClick('backpack')}>
          <span>🎒</span>
        </div>
      </div>

      {/* Drawer */}
      <div className={`side-drawer ${open ? 'open' : ''}`}>
        <div className="side-drawer-header">
          <span>{activeTab === 'campaign' ? '📖 Campaign Guide' : activeTab === 'menagerie' ? '👹 Menagerie' : activeTab === 'backpack' ? '🎒 Backpack' : activeTab === 'cartography' ? '🗺️ Cartography' : '🌑 Omens'}</span>
          <button className="side-drawer-close" onClick={() => setOpen(false)}>✕</button>
        </div>

        {activeTab === 'campaign' && (
          <div className="side-drawer-body">
            {campaignNotes ? (
              <>
                {sections.length > 0 && (
                  <div className="campaign-toc">
                    <div className="campaign-toc-title">Contents</div>
                    {sections.map((s, i) => (
                      <div key={i} className="campaign-toc-item" onClick={() => scrollToSection(s)}>• {s}</div>
                    ))}
                  </div>
                )}
                <div className="campaign-content" ref={contentRef} dangerouslySetInnerHTML={{ __html: renderMarkdown(campaignNotes) }} />
                <div className="campaign-ai-bar">
                  {aiResult && (
                    <div className="campaign-ai-result">
                      <div className="campaign-ai-result-header">✨ AI Suggestions <span onClick={() => setAiResult('')} style={{cursor:'pointer',float:'right'}}>✕</span></div>
                      <div dangerouslySetInnerHTML={{ __html: renderMarkdown(aiResult) }} />
                    </div>
                  )}
                  <button className="campaign-ai-btn" disabled={aiLoading} onClick={() => {
                    const session = localStorage.getItem('session');
                    if (!session || !campaignNotes) return;
                    setAiLoading(true);
                    setAiResult('');
                    fetch(`https://lzj7bud7wsvt3mgoybexxemfei0ilunt.lambda-url.us-east-1.on.aws/`, {
                      method: 'POST',
                      headers: { 'Authorization': 'Bearer ' + session, 'Content-Type': 'application/json' },
                      body: JSON.stringify({ context: campaignNotes, action: 'improve' })
                    })
                      .then(r => r.json())
                      .then(d => { setAiResult(d.result || d.error || 'No response'); setAiLoading(false); })
                      .catch(() => { setAiResult('Error connecting to AI.'); setAiLoading(false); });
                  }}>{aiLoading ? '⏳ Analyzing...' : '✨ Improve'}</button>
                </div>
              </>
            ) : (
              <div className="campaign-empty">
                <p>No campaign notes uploaded.</p>
                <p style={{ fontSize: '0.65rem', color: 'var(--text-dim)' }}>Go to Settings → Campaign to add your notes.</p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'menagerie' && (
          <div className="side-drawer-body" style={{ padding: '12px 16px' }}>
            <p style={{ color: 'var(--text-dim)', fontSize: '0.65rem', marginBottom: '12px' }}>Click to spawn. Tap multiple times for groups.</p>
            <div className="menagerie-list">
              {ENEMIES.map(e => (
                <div key={e} className="menagerie-item" onClick={() => onCommand(`!sp ${e}`)}>
                  <span className="menagerie-name">{e}</span>
                  <span className="menagerie-spawn">⚔️</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'cartography' && (
          <div className="side-drawer-body" style={{ padding: '16px' }}>
            <p style={{ color: 'var(--text-dim)', fontSize: '0.72rem' }}>🗺️ Map viewer coming soon.</p>
          </div>
        )}
        {activeTab === 'omens' && (
          <div className="side-drawer-body">
            <p style={{ color: 'var(--text-dim)', fontSize: '0.72rem', marginBottom: '16px', padding: '0 16px' }}>
              Narrative mechanics that bend the story in unexpected ways.
            </p>
            <div style={{ padding: '0 16px' }}>
              {OMENS.map(o => (
                <div key={o.name} className="omen-card" onClick={() => { onCommand(o.cmd); }}>
                  <span className="omen-emoji">{o.emoji}</span>
                  <div className="omen-info">
                    <div className="omen-name">{o.name}</div>
                    <div className="omen-desc">{o.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        {activeTab === 'backpack' && (
          <div className="side-drawer-body" style={{ padding: '16px' }}>
            {players && players.length > 0 ? (isDM ? players : players.filter(p => p.discord_id === currentUserId)).map(p => {
              const items = Array.isArray(p.inventory) ? p.inventory.filter(Boolean) : (p.inventory || '').split(',').map(i => i.trim()).filter(Boolean);
              const RARITY_COLORS = {artifact:'#e6cc00',legendary:'#e67e22',very_rare:'#9b59b6',rare:'#3498db',uncommon:'#2ecc71',common:'#aaa'};
              const ITEM_RARITY = {'sword of testing':'rare','shield of debugging':'rare','helmet of feedback':'uncommon','potion of qa':'uncommon','resurrection scroll':'rare','smoke bomb':'uncommon','tincture of clarity':'rare','everlight torch':'uncommon','adaptable satchel':'uncommon','antidote vial':'common','whetstone':'common','tidecaller axe':'uncommon','coral blade':'rare','harpoon of the deep':'uncommon','ring of swimming':'uncommon','splinterfang hatchet':'uncommon','mirrorbane rapier':'rare','voidcleaver greatsword':'very_rare','hollowshot longbow':'very_rare',"aldric's paradox":'legendary','soulcrusher maul':'very_rare','fractal wand':'uncommon','copycat shortbow':'rare','echo staff':'rare','mindbreak staff':'very_rare','hungering blade':'very_rare','tome of unmaking':'very_rare','mantle of the unkillable':'artifact','voidstrider boots':'artifact','worldbreaker gauntlets':'artifact','crown of the eternal':'artifact','aegis of the forgotten god':'artifact','ring of infinite recursion':'artifact',"oathbreaker's ruin":'artifact','voidweave robes':'rare',"sigrid's resolve":'legendary','lucky coin':'uncommon',"diplomat's brooch":'uncommon',"tinker's lens":'uncommon'};
              const getRarityColor = (name) => { const r = ITEM_RARITY[name.toLowerCase()]; return r ? RARITY_COLORS[r] : null; };
              const equippedItems = new Set([p.equipped_weapon, p.equipped_armor, p.equipped_offhand, p.equipped_head, p.equipped_hands, p.equipped_feet, p.equipped_ring].filter(Boolean).map(e => e.toLowerCase()));
              return (
                <div key={p.discord_id} style={{ marginBottom: '16px' }}
                  onDragOver={isDM ? (e) => { e.preventDefault(); e.currentTarget.style.background = 'rgba(212,175,55,0.1)'; } : undefined}
                  onDragLeave={isDM ? (e) => { e.currentTarget.style.background = ''; } : undefined}
                  onDrop={isDM ? (e) => { e.preventDefault(); e.currentTarget.style.background = '';
                    const data = JSON.parse(e.dataTransfer.getData('text/plain') || '{}');
                    if (data.item && data.owner !== p.char_name) { onCommand(`!equip ${data.item} from ${data.owner} on ${p.char_name}`); }
                  } : undefined}
                >
                  <div style={{ color: 'var(--accent)', fontSize: '0.75rem', marginBottom: '8px', borderBottom: '1px solid var(--panel-border)', paddingBottom: '4px' }}>{p.char_name}</div>
                  {items.length > 0 ? (() => {
                    const stacked = [];
                    const counts = {};
                    items.forEach(item => { const k = item.toLowerCase(); counts[k] = (counts[k] || 0) + 1; if (counts[k] === 1) stacked.push(item); });
                    return stacked.map((item, i) => {
                    const il = item.toLowerCase();
                    const count = counts[il];
                    const isEquippable = ['sword','axe','mace','hammer','blade','dagger','spear','bow','staff','wand','shield','plate','chain','leather','armor','mantle','mail','helm','helmet','crown','gauntlet','glove','boot','ring','aegis','buckler','breastplate','robe','hood','bracer','worldbreaker','voidstrider','recursion','edge','ruin','whisper','harpoon','maul','rapier','tome','longbow','shortbow','hatchet'].some(w => il.includes(w));
                    const icon = il.includes('potion') ? '🧪' : il.includes('scroll') ? '📜' : il.includes('sword') || il.includes('blade') || il.includes('dagger') || il.includes('rapier') || il.includes('harpoon') || il.includes('maul') || il.includes('hatchet') || il.includes('axe') ? '🗡️' : il.includes('bow') || il.includes('arrow') ? '🏹' : il.includes('staff') || il.includes('wand') || il.includes('tome') ? '🪄' : il.includes('shield') || il.includes('aegis') ? '🛡️' : il.includes('armor') || il.includes('plate') || il.includes('mail') || il.includes('mantle') || il.includes('robe') ? '🦺' : il.includes('ring') || il.includes('recursion') ? '💍' : il.includes('helm') || il.includes('crown') || il.includes('hood') ? '👑' : il.includes('boot') || il.includes('voidstrider') ? '👢' : il.includes('gauntlet') || il.includes('glove') || il.includes('worldbreaker') || il.includes('bracer') ? '🧤' : il.includes('key') ? '🗝️' : '📦';
                    const isEquipped = equippedItems.has(il);
                    return (
                      <div key={i} draggable={isDM} onDragStart={isDM ? (e) => e.dataTransfer.setData('text/plain', JSON.stringify({item, owner: p.char_name})) : undefined} onClick={() => isEquippable && setEquipPending({item, owner: p.char_name})} style={{ fontSize: '0.7rem', color: getRarityColor(item) || 'var(--text)', padding: '4px 8px', marginBottom: '3px', background: isEquipped ? 'rgba(212,175,55,0.1)' : 'rgba(255,255,255,0.02)', borderRadius: '4px', border: isEquipped ? '1px solid rgba(212,175,55,0.4)' : '1px solid var(--panel-border)', cursor: isDM ? 'grab' : isEquippable ? 'pointer' : 'default' }}>
                        {icon} {item.replace(/\b\w/g, c => c.toUpperCase()).replace(/\b(Of|The|A|An|And|In|On|To|For|From)\b/g, m => m.toLowerCase()).replace(/'S\b/g, "'s")}{count > 1 && <span style={{color:'var(--text-dim)',marginLeft:'4px'}}>x{count}</span>} {isEquipped && <span style={{fontSize:'0.55rem',color:'#d4af37',float:'right'}}>equipped</span>} {!isEquipped && isEquippable && <span style={{fontSize:'0.55rem',color:'var(--text-dim)',float:'right'}}>tap to equip</span>}
                      </div>
                    );
                  }); })() : <p style={{ fontSize: '0.65rem', color: 'var(--text-dim)' }}>No items yet.</p>}
                  {p.gold > 0 && <div style={{ fontSize: '0.7rem', color: '#d4af37', marginTop: '6px' }}>💰 {p.gold} gold</div>}
                </div>
              );
            }) : <p style={{ fontSize: '0.7rem', color: 'var(--text-dim)' }}>No characters in room.</p>}
            {equipPending && (
              <div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 2000, background: 'var(--panel)', border: '1px solid var(--accent)', borderRadius: '10px', padding: '16px', minWidth: '200px', boxShadow: '0 4px 20px rgba(0,0,0,0.6)' }}>
                <div style={{ fontSize: '0.75rem', color: 'var(--text)', marginBottom: '12px', textAlign: 'center' }}>Equip <strong>{equipPending.item.replace(/\b\w/g, c => c.toUpperCase()).replace(/\b(Of|The|A|An|And|In|On|To|For|From)\b/g, m => m.toLowerCase()).replace(/'S\b/g, "'s")}</strong> on:</div>
                {players.map(p => (
                  <div key={p.discord_id} onClick={() => { 
                    onCommand(`!equip ${equipPending.item} from ${equipPending.owner} on ${p.char_name}`);
                    setEquipPending(null); 
                  }} style={{ padding: '8px 12px', marginBottom: '6px', background: 'rgba(255,255,255,0.03)', border: '1px solid var(--panel-border)', borderRadius: '6px', cursor: 'pointer', fontSize: '0.72rem', color: 'var(--text)' }}>
                    {p.char_name === equipPending.owner ? '🛡️' : '⚔️'} {p.char_name} {p.char_name === equipPending.owner ? <span style={{fontSize:'0.6rem',color:'var(--accent)'}}>(equip to slot)</span> : <span style={{ color: 'var(--text-dim)', fontSize: '0.6rem' }}>(transfer & equip)</span>}
                  </div>
                ))}
                <div onClick={() => setEquipPending(null)} style={{ textAlign: 'center', fontSize: '0.65rem', color: 'var(--text-dim)', cursor: 'pointer', marginTop: '8px' }}>Cancel</div>
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
}
